<template>
  <div class="home-view container" ref="scrollComponent">
    <WalletConnectCard />

    <div
      class="d-flex flex-lg-row flex-wrap align-center ga-2 ga-lg-6 justify-space-between"
    >
      <v-btn
        block
        variant="outlined"
        class="order-first mb-4 d-md-none"
        :to="{ name: ROUTE_CREATE_TOKEN }"
      >
        {{ $t("blocks.appHeader.createTokenMobile") }}
      </v-btn>
      <h2
        v-text="$t('common.tokenList')"
        class="order-first text-h4 mr-md-12"
      />

      <!-- FILTERS -->
      <div
        class="order-last order-lg-1 w-100 w-lg-auto d-flex ga-2 flex-row filters"
      >
        <v-menu v-if="activeFactoryCollectionsNum > 1">
          <template #activator="{ props }">
            <v-btn
              variant="outlined"
              :size="smAndUp ? 'large' : undefined"
              class="px-1 px-sm-4 flex-grow-1"
              v-bind="props"
            >
              {{ activeCollectionOption?.title }}
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="({ disabled, title, value }, index) in collectionOptions"
              :key="index"
              :disabled="disabled"
              @click="updateCollection(value)"
            >
              <v-list-item-title
                :class="{
                  'text-primary': collection == value,
                }"
              >
                {{ title }}
                <small class="pl-2" v-if="disabled">
                  ({{ $t("common.comingSoon") }})
                </small>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          v-if="activeAccount"
          variant="outlined"
          :size="smAndUp ? 'large' : undefined"
          class="px-1 px-sm-4 flex-grow-1"
          style="min-width: auto"
          :color="ownedOnly ? 'primary' : undefined"
          @click="ownedOnly = !ownedOnly"
        >
          {{
            smAndUp
              ? $t("views.home.showOwnedOnly")
              : $t("views.home.showOwnedOnlyShort")
          }}
        </v-btn>

        <!-- OrderBy -->
        <v-menu>
          <template #activator="{ props }">
            <v-btn
              variant="outlined"
              :size="smAndUp ? 'large' : undefined"
              class="px-1 px-sm-4 flex-grow-1"
              v-bind="props"
            >
              {{ activeSortOption?.title }}
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="({ title, value }, index) in orderByOptions"
              :key="index"
              @click="updateOrderBy(value)"
            >
              <v-list-item-title
                :class="{
                  'text-primary': orderBy === value,
                }"
              >
                {{ title }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <!-- /FILTERS -->

      <div class="order-2 ml-auto pl-2">
        <v-text-field
          v-model="search"
          :placeholder="$t('views.home.searchLabel')"
          variant="outlined"
          append-inner-icon="mdi-magnify"
          density="compact"
          class="search-field d-block h-100"
          hide-details
        />
      </div>
    </div>

    <v-row class="position-relative mt-sm-1 mt-md-4">
      <v-alert
        v-if="
          (!data?.pages?.length || !data?.pages[0].items.length) && !isFetching
        "
        v-text="$t('views.home.noTokenSales')"
        type="info"
        outlined
        class="ma-3"
      />

      <v-col cols="12">
        <TokenListTable :pages="data?.pages" :loading="isFetching" />
      </v-col>
    </v-row>

    <div class="text-center">
      <v-btn
        v-if="hasNextPage"
        v-text="$t('common.loadMore')"
        variant="outlined"
        rounded
        size="large"
        :loading="isFetching"
        @click="fetchNextPage"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { refThrottled } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { Encoded } from "@aeternity/aepp-sdk";

import type { ObjectValues } from "@/utils/types";
import { ROUTE_CREATE_TOKEN } from "@/utils/constants";
import { includes } from "@/utils";
import { TokensService } from "@/api/generated";
import WebSocketClient from "@/libs/WebSocketClient";
import { useCommunityFactory, useNetwork } from "@/composables";
import { useAccounts } from "@/stores";

import WalletConnectCard from "@/components/WalletConnect/WalletConnectCard.vue";
import TokenListTable from "@/components/Tokens/TokenListTable.vue";

type SelectOptions<T> = {
  title: string;
  disabled?: boolean;
  value: T;
}[];

const SORT = {
  marketCap: "market_cap",
  newest: "newest",
  oldest: "oldest",
} as const;
type OrderByOption = ObjectValues<typeof SORT>;
type CollectionOption = "all" | `${string}-${Encoded.AccountAddress}`;

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { smAndUp } = useDisplay();
const { activeNetworkId } = useNetwork();
const { activeAccount } = storeToRefs(useAccounts());
const { activeFactorySchema, activeFactoryCollectionsNum } =
  useCommunityFactory();

const collection = ref<CollectionOption>(
  (route.query.collection as any) ?? "all"
);
const orderBy = ref<OrderByOption>((route.query.sort as any) ?? SORT.marketCap);
const ownedOnly = ref(false);
const search = ref<string>(route.query.search?.toString() ?? "");
const searchThrottled = refThrottled(search, 2000);

function updateCollection(val: CollectionOption) {
  collection.value = val;
  router.push({
    query: { ...route.query, collection: val },
  });
}

function updateOrderBy(val: OrderByOption) {
  orderBy.value = val;
  router.push({
    query: { ...route.query, sort: val },
  });
}

const orderByOptions: SelectOptions<OrderByOption> = [
  // {
  //   title: t("common.name"),
  //   value: "name",
  // },
  {
    title: t("common.marketCap"),
    value: "market_cap",
  },
  {
    title: t("common.newest"),
    value: "newest",
  },
  {
    title: t("common.oldest"),
    value: "oldest",
  },
  // {
  //   title: t("common.rank"),
  //   value: "rank",
  // },
  // {
  //   title: t("common.creationDate"),
  //   value: "created_at",
  // },
];

const collectionOptions = computed<SelectOptions<CollectionOption>>(() => {
  const collections = activeFactorySchema.value?.collections || {};
  return [
    {
      title: t("common.allTokens"),
      value: "all",
    },
    ...Object.values(collections).map((collection) => ({
      title: t(`collections.${collection.name}.only`),
      value: collection.id as CollectionOption,
    })),
  ];
});

const activeCollectionOption = computed(() =>
  collectionOptions.value.find((option) => option.value === collection.value)
);
const activeSortOption = computed(() =>
  orderByOptions.find((option) => option.value === orderBy.value)
);

const scrollComponent = ref<HTMLElement>();
const newTokenListenerSubscription = ref();
let refetchTimeout: NodeJS.Timeout;

const orderByMapped = computed(() =>
  includes([SORT.newest, SORT.oldest], orderBy.value)
    ? "created_at"
    : orderBy.value
);

const orderDirection = computed((): "ASC" | "DESC" =>
  orderBy.value === "oldest" ? "ASC" : "DESC"
);

const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
  useInfiniteQuery({
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      TokensService.listAll({
        orderBy: orderByMapped.value as any,
        orderDirection: orderDirection.value,
        collection: collection.value,
        search: searchThrottled.value,
        ownerAddress: ownedOnly.value
          ? (activeAccount.value as string)
          : undefined,
        limit: 20,
        page: pageParam,
      }),
    getNextPageParam: (lastPage: any, allPages, lastPageParam) => {
      if (lastPage?.meta?.currentPage === lastPage?.meta?.totalPages) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    queryKey: [
      "TokensService.listAll",
      orderBy,
      collection,
      orderByMapped,
      searchThrottled,
      activeNetworkId,
      ownedOnly,
      activeAccount,
    ],
  });

const handleScroll = () => {
  if (isFetching.value) {
    return;
  }
  clearTimeout(refetchTimeout);
  refetchTimeout = setTimeout(() => {
    if (
      (hasNextPage.value &&
        scrollComponent.value?.getBoundingClientRect()?.bottom) ||
      0 < window.innerHeight
    ) {
      fetchNextPage();
    }
  }, 500);
};

watch(searchThrottled, (val, oldVal) => {
  if (val !== oldVal) {
    router.push({
      query: { ...route.query, search: val },
    });
  }
});

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  newTokenListenerSubscription.value = WebSocketClient.subscribeToNewTokenSales(
    (token: any) => {
      console.log("New token sale::", token);
      clearTimeout(refetchTimeout);
      refetchTimeout = setTimeout(() => {
        refetch();
      }, 4000);
    }
  );
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  newTokenListenerSubscription.value?.();
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables" as *;

.home-view {
  .search-field {
    min-width: 220px;
  }

  .table-title {
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 100%; /* 14px */
    letter-spacing: 0.42px;
    opacity: 0.8;
    margin-bottom: -4px;
  }

  .loading-layer {
    position: absolute;
    z-index: 2;
    inset: 0;
    padding-top: 100px;
    background: rgba(var(--v-theme-background), 0.5);
  }

  .filters {
    .v-btn {
      color: rgba(white, 0.8);
    }
  }

  @media screen and (max-width: 460px) {
    .search-field {
      min-width: 150px;
    }

    .filters {
      .v-btn {
        font-size: 9px !important;
      }
    }
  }
}
</style>
